package domain.score

import engine.model.Drop
import engine.model.MoveType
import engine.model.ScoringRuleBook
import engine.model.SpinType

// ── Gravitar move type vocabulary ─────────────────────────────────────────────

/**
 * All move types recognised by Gravitar's story rulebook.
 *
 * Line-clear naming uses a space/orbital scale — each tier is 4 lines apart,
 * matching the board's 20-row geometry (4 rows = one visual "zone"):
 *
 *   1–3  lines  →  BLIP / BURST / FLARE        (sub-tier, no bucket)
 *   4–7  lines  →  GRAVITAR    (the game's signature clear)
 *   8–11 lines  →  VOID        (half the board collapsed)
 *   12–15 lines →  COLLAPSE    (three zones gone)
 *   16–19 lines →  EVENT       (approaching the horizon)
 *   20–23 lines →  HORIZON     (four full zones — board nearly wiped)
 *   24+  lines  →  OBLIVION    (freeze-mode theoretical maximum)
 *
 * Spin clears use the "PULL" prefix — a piece pulling into orbit against gravity.
 */
enum class GravitarMoveType(
    override val id: String,
    override val displayName: String,
    override val isSpecial: Boolean,
) : MoveType {

    // ── Zero-line placement ───────────────────────────────────────────────────
    NONE(
        id          = "none",
        displayName = "",
        isSpecial   = false,
    ),

    // ── Sub-tier line clears (1–3, no bucket) ────────────────────────────────
    BLIP(
        id          = "blip",
        displayName = "BLIP",
        isSpecial   = false,
    ),
    BURST(
        id          = "burst",
        displayName = "BURST",
        isSpecial   = false,
    ),
    FLARE(
        id          = "flare",
        displayName = "FLARE",
        isSpecial   = false,
    ),

    // ── Tier 1 — 4–7 lines ───────────────────────────────────────────────────
    /** The game's namesake clear. Base score awarded for 4–7 line clears. */
    GRAVITAR(
        id          = "gravitar",
        displayName = "GRAVITAR",
        isSpecial   = true,
    ),

    // ── Tier 2 — 8–11 lines ──────────────────────────────────────────────────
    /** Half the board or more collapsed in one clear. */
    VOID(
        id          = "void",
        displayName = "VOID",
        isSpecial   = true,
    ),

    // ── Tier 3 — 12–15 lines ─────────────────────────────────────────────────
    /** Three board-zones cleared simultaneously. */
    COLLAPSE(
        id          = "collapse",
        displayName = "COLLAPSE",
        isSpecial   = true,
    ),

    // ── Tier 4 — 16–19 lines ─────────────────────────────────────────────────
    /** Approaching the event horizon. */
    EVENT(
        id          = "event",
        displayName = "EVENT HORIZON",
        isSpecial   = true,
    ),

    // ── Tier 5 — 20–23 lines ─────────────────────────────────────────────────
    /** Near-total board wipe. Score awarded for anything in the 20–24 range. */
    HORIZON(
        id          = "horizon",
        displayName = "HORIZON",
        isSpecial   = true,
    ),

    // ── Tier 6 — 24 lines (freeze-mode theoretical maximum) ──────────────────
    /** Full board annihilation — only reachable in freeze mode. */
    OBLIVION(
        id          = "oblivion",
        displayName = "OBLIVION",
        isSpecial   = true,
    ),

    // ── Spin clears ───────────────────────────────────────────────────────────
    PULL_MINI(
        id          = "pull_mini",
        displayName = "MINI PULL",
        isSpecial   = true,
    ),
    PULL(
        id          = "pull",
        displayName = "PULL",
        isSpecial   = true,
    ),
    PULL_DOUBLE(
        id          = "pull_double",
        displayName = "PULL DOUBLE",
        isSpecial   = true,
    ),
    PULL_TRIPLE(
        id          = "pull_triple",
        displayName = "PULL TRIPLE",
        isSpecial   = true,
    ),
}

// ── Scoring tier model ────────────────────────────────────────────────────────

/**
 * Maps a raw line count to its [GravitarMoveType] bucket and base point value.
 *
 * Bucket boundaries (step of 4, matching board row zones):
 *
 *   Lines     Bucket       Base pts
 *   ──────────────────────────────────
 *   1         BLIP          100
 *   2         BURST         300
 *   3         FLARE         500
 *   4–7       GRAVITAR      800
 *   8–11      VOID        2 000
 *   12–15     COLLAPSE    4 500
 *   16–19     EVENT      10 000
 *   20–23     HORIZON    22 000
 *   24+       OBLIVION   50 000
 */
private data class ScoringTier(
    val moveType:   GravitarMoveType,
    val basePoints: Double,
)

private fun resolveTier(lines: Int): ScoringTier = when {
    lines >= 24 -> ScoringTier(GravitarMoveType.OBLIVION,  50_000.0)
    lines >= 20 -> ScoringTier(GravitarMoveType.HORIZON,   22_000.0)
    lines >= 16 -> ScoringTier(GravitarMoveType.EVENT,     10_000.0)
    lines >= 12 -> ScoringTier(GravitarMoveType.COLLAPSE,   4_500.0)
    lines >= 8  -> ScoringTier(GravitarMoveType.VOID,       2_000.0)
    lines >= 4  -> ScoringTier(GravitarMoveType.GRAVITAR,     800.0)
    lines == 3  -> ScoringTier(GravitarMoveType.FLARE,        500.0)
    lines == 2  -> ScoringTier(GravitarMoveType.BURST,        300.0)
    lines == 1  -> ScoringTier(GravitarMoveType.BLIP,         100.0)
    else        -> ScoringTier(GravitarMoveType.NONE,           0.0)
}

// ── Rulebook ──────────────────────────────────────────────────────────────────

/**
 * Story-mode scoring rulebook.
 *
 * - Base points use the 6-tier bucket system above (step of 4 lines per tier).
 * - Spin ("PULL") clears add a flat bonus on top of the resolved tier base.
 * - Back-to-back 1.5× on difficult clears (tier GRAVITAR+ or any PULL).
 * - Combo bonus: level × combo × [comboFactor].
 * - Perfect-clear bonus when the board is empty after a clear.
 */
class StoryRulebook : ScoringRuleBook {

    override val dropTables: Map<Drop, Double> = emptyMap()

    companion object {
        /** Flat spin bonus added on top of the tier base. */
        private val SPIN_BONUS = mapOf(
            SpinType.MINI to  100.0,
            SpinType.FULL to  400.0,
            SpinType.NONE to    0.0,
        )
    }

    override val b2bMultiplier:     Double get() = 1.5
    override val comboFactor:       Double get() = 50.0
    override val perfectClearBonus: Double get() = 3500.0

    override fun getBasePoints(spinType: SpinType, lines: Int): Double {
        val tier      = resolveTier(lines)
        val spinBonus = SPIN_BONUS[spinType] ?: 0.0
        return tier.basePoints + spinBonus
    }

    /** A clear is "difficult" if it is tier GRAVITAR (4+ lines) or any spin. */
    override fun isDifficult(spinType: SpinType, lines: Int): Boolean =
        lines >= 4 || spinType == SpinType.FULL || spinType == SpinType.MINI

    /**
     * Resolves the move type.
     *
     * Spin clears take priority over line-count tiers.
     * Among spin clears, full spins are preferred over mini.
     * Line-count clears resolve through [resolveTier].
     */
    override fun getMoveType(spinType: SpinType, lines: Int, pieceName: String): MoveType =
        when {
            spinType == SpinType.FULL && lines >= 3 -> GravitarMoveType.PULL_TRIPLE
            spinType == SpinType.FULL && lines == 2 -> GravitarMoveType.PULL_DOUBLE
            spinType == SpinType.FULL && lines >= 1 -> GravitarMoveType.PULL
            spinType == SpinType.MINI && lines >= 1 -> GravitarMoveType.PULL_MINI
            else                                    -> resolveTier(lines).moveType
        }
}
